import {
  Mail,
  CheckCircle,
  Users,
  Copy as CopyIcon,
  Eye,
  Edit3,
  Trash2,
  Clock,
  Link2,
  Settings,
  X,
  ChevronDown
} from 'lucide-react';
import { useState, useEffect } from 'react';
import * as TripInvitationService from '../../../services/trip-invitation.service';
import tripsService from '../../../services/trips/trips.service';
import ModalCustom from '../../UI/modal';
import Button from '../../UI/button';

interface ShareInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  tripId: string;
  tripName: string;
  onOpenPublicSettings?: () => void;
}

interface TripOwner {
  user_id: string;
  email: string;
  username: string;
}

interface Collaborator {
  id: string;
  user_id: string;
  permission: string;
  email?: string;
  created_at: string;
}

interface PendingInvitation {
  id: string;
  recipient_email: string;
  sharing_mode: string;
  created_at: string;
  status: string;
}

function ShareInviteModal({
  isOpen,
  onClose,
  tripId,
  tripName,
  onOpenPublicSettings
}: ShareInviteModalProps) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [message, setMessage] = useState('');
  const [sharingMode, setSharingMode] = useState<'copy' | 'read' | 'edit'>(
    'copy'
  );
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<
    PendingInvitation[]
  >([]);
  const [loadingCollaborators, setLoadingCollaborators] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [cancelingId, setCancelingId] = useState<string | null>(null);
  const [tripOwner, setTripOwner] = useState<TripOwner | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadTripDetails();
      loadPendingInvitations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tripId]);

  const loadTripDetails = async () => {
    setLoadingCollaborators(true);
    try {
      // Get full trip details with permissions
      const trip = await tripsService.getTripById(tripId);

      // Extract owner and collaborators from trip permissions
      if (trip.permissions && trip.permissions.length > 0) {
        // Find owner
        const owner = trip.permissions.find((p) => p.role === 'owner');
        if (owner) {
          setTripOwner({
            user_id: owner.userId,
            email: owner.username || owner.userId,
            username: owner.username || owner.userId
          });
        }

        // Get all non-owner collaborators from permissions
        const collabs = trip.permissions
          .filter((p) => p.role !== 'owner')
          .map((p) => ({
            id: p.userId,
            user_id: p.userId,
            permission: p.role === 'contributor' ? 'edit' : 'view',
            email: p.username || p.userId,
            created_at: p.grantedAt?.toString() || ''
          }));

        setCollaborators(collabs);
      }
    } catch (error) {
      console.error('Error loading trip details:', error);
      setCollaborators([]);
    } finally {
      setLoadingCollaborators(false);
    }
  };

  const loadPendingInvitations = async () => {
    try {
      const invitations = await TripInvitationService.GetTripPendingInvitations(
        tripId
      );

      const pendingInvites = invitations.map((inv: any) => ({
        id: inv.id || inv._id,
        recipient_email: inv.inviteeEmail || inv.recipient_email,
        sharing_mode: inv.role === 'contributor' ? 'edit' : 'read',
        created_at: inv.createdAt || inv.created_at,
        status: inv.status
      }));

      setPendingInvitations(pendingInvites);
    } catch (error) {
      console.error('Error loading pending invitations:', error);
      setPendingInvitations([]);
    }
  };

  const handleRemoveCollaborator = async (userId: string) => {
    if (!window.confirm('Are you sure you want to remove this collaborator?'))
      return;

    setRemovingId(userId);
    try {
      // Use the DDD trip permissions API to remove the permission
      await TripInvitationService.RemovePermission(tripId, userId);

      await loadTripDetails();
      setMessage('Collaborator removed successfully');
      setTimeout(() => {
        setMessage('');
      }, 2000);
    } catch (error) {
      console.error('Error removing collaborator:', error);
      setMessage('Failed to remove collaborator');
    } finally {
      setRemovingId(null);
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    if (!window.confirm('Are you sure you want to cancel this invitation?'))
      return;

    setCancelingId(invitationId);
    try {
      await TripInvitationService.RevokeInvitation(tripId, invitationId);

      await loadPendingInvitations();
      setMessage('Invitation cancelled successfully');
      setTimeout(() => {
        setMessage('');
      }, 2000);
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      setMessage('Failed to cancel invitation');
    } finally {
      setCancelingId(null);
    }
  };

  if (!isOpen) return null;

  const handleShare = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const emails = email
        .split(',')
        .map((e) => e.trim().toLowerCase())
        .filter((e) => e);

      if (emails.length === 0) {
        setMessage('Please enter at least one email address');
        setLoading(false);
        return;
      }

      let successCount = 0;
      let failCount = 0;

      for (const recipientEmail of emails) {
        try {
          if (sharingMode === 'copy') {
            // Send trip copy with clean flag set to true
            await tripsService.sendTripCopy(tripId, recipientEmail, true);
          } else {
            // Map sharing mode to role: 'edit' -> 'contributor', 'read' -> 'follower'
            const role = sharingMode === 'edit' ? 'contributor' : 'follower';

            await TripInvitationService.InviteCollaborator({
              tripId: tripId,
              inviteeEmail: recipientEmail,
              invitationRole: role
            });
          }

          successCount++;
        } catch (err) {
          console.error(`Error sending to ${recipientEmail}:`, err);
          failCount++;
        }
      }

      if (successCount > 0) {
        if (successCount === 1) {
          setMessage(
            sharingMode === 'copy'
              ? `Trip copy sent to ${emails[0]}!`
              : `Invitation sent to ${emails[0]}!`
          );
        } else {
          setMessage(
            sharingMode === 'copy'
              ? `Trip ${
                  successCount > 1 ? 'copies' : 'copy'
                } sent successfully!`
              : `${successCount} invitation${
                  successCount > 1 ? 's' : ''
                } sent successfully!`
          );
        }
        setEmail('');
        if (sharingMode !== 'copy') {
          await loadTripDetails();
          await loadPendingInvitations();
        }
      }

      if (failCount > 0) {
        setMessage(
          `Failed to send ${failCount} ${
            sharingMode === 'copy' ? 'copy' : 'invitation'
          }${
            failCount > 1 ? (sharingMode === 'copy' ? 'copies' : 's') : ''
          }. Please try again.`
        );
      }

      setTimeout(() => {
        setMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error sharing trip:', error);
      setMessage(
        `Failed to send ${
          sharingMode === 'copy' ? 'trip copy' : 'invitation'
        }. Please try again.`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalCustom
      show={isOpen}
      onHide={() => onClose()}
      title='Share Trip'
      size='lg'
      backdrop={true}
    >
      <p className='text-muted mb-4'>
        Choose how you want to share "{tripName}" with another user.
      </p>

      {(tripOwner || collaborators.length > 0) && (
        <div className='mb-4 p-3 rounded border'>
          <button
            type='button'
            className='btn btn-link p-0 text-decoration-none w-100 text-start'
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <div className='d-flex align-items-center justify-content-between'>
              <div className='d-flex align-items-center gap-2'>
                <Users
                  size={18}
                  className='text-primary'
                />
                <h6 className='fw-semibold mb-0 text-dark'>
                  People with Access (
                  {(tripOwner ? 1 : 0) + collaborators.length})
                </h6>
              </div>
              <ChevronDown
                size={18}
                className={`text-primary transition-transform ${
                  isExpanded ? 'rotate-180' : ''
                }`}
                style={{
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              />
            </div>
          </button>
          {isExpanded &&
            (loadingCollaborators ? (
              <p className='small text-muted mb-0 mt-3'>Loading...</p>
            ) : (
              <div
                className='overflow-auto mt-3'
                style={{ maxHeight: '200px' }}
              >
                {tripOwner && (
                  <div className='d-flex align-items-center justify-content-between p-2 bg-white rounded border mb-2'>
                    <div className='d-flex align-items-center gap-2 flex-fill'>
                      <div
                        className='rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 bg-success'
                        style={{ width: '32px', height: '32px' }}
                      >
                        <span className='text-white small fw-semibold'>
                          {tripOwner.username.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className='flex-fill text-truncate'>
                        <p className='small fw-medium mb-0 text-truncate'>
                          {tripOwner.username}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {collaborators.map((collab) => {
                  const username = collab.email || 'Unknown';
                  return (
                    <div
                      key={collab.id}
                      className='d-flex align-items-center justify-content-between p-2 bg-white rounded border mb-2'
                    >
                      <div className='d-flex align-items-center gap-2 flex-fill'>
                        <div
                          className='rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 bg-primary'
                          style={{ width: '32px', height: '32px' }}
                        >
                          <span className='text-white small fw-semibold'>
                            {username.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className='flex-fill text-truncate'>
                          <p className='small fw-medium mb-0 text-truncate'>
                            {username}
                          </p>
                          <div className='d-flex align-items-center gap-1'>
                            {collab.permission === 'view' ? (
                              <>
                                <Eye
                                  size={12}
                                  className='text-muted'
                                />
                                <span
                                  className='text-muted'
                                  style={{ fontSize: '0.7rem' }}
                                >
                                  Read-Only
                                </span>
                              </>
                            ) : (
                              <>
                                <Edit3
                                  size={12}
                                  className='text-muted'
                                />
                                <span
                                  className='text-muted'
                                  style={{ fontSize: '0.7rem' }}
                                >
                                  Full Access
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleRemoveCollaborator(collab.user_id)}
                        disabled={removingId === collab.user_id}
                        variant='transparent'
                        size='sm'
                        className='text-danger p-1'
                        title='Remove collaborator'
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
      )}

      <div className='mb-4'>
        <label className='form-label fw-medium'>Sharing Mode</label>
        <div className='d-flex flex-column gap-2'>
          <button
            type='button'
            onClick={() => setSharingMode('copy')}
            className={`btn text-start p-3 border ${
              sharingMode === 'copy'
                ? 'border-primary border-2 bg-primary-subtle'
                : 'border-secondary'
            }`}
          >
            <div className='d-flex align-items-start gap-2'>
              <CopyIcon
                size={18}
                className={`flex-shrink-0 mt-1 ${
                  sharingMode === 'copy' ? 'text-primary' : 'text-muted'
                }`}
              />
              <div className='flex-fill'>
                <div className='fw-semibold small mb-1'>Complete Copy</div>
                <div
                  className='text-muted'
                  style={{ fontSize: '0.8rem', lineHeight: '1.4' }}
                >
                  They get their own independent copy to edit and modify as they
                  wish
                </div>
              </div>
            </div>
          </button>

          <button
            type='button'
            onClick={() => setSharingMode('read')}
            className={`btn text-start p-3 border ${
              sharingMode === 'read'
                ? 'border-primary border-2 bg-primary-subtle'
                : 'border-secondary'
            }`}
          >
            <div className='d-flex align-items-start gap-2'>
              <Eye
                size={18}
                className={`flex-shrink-0 mt-1 ${
                  sharingMode === 'read' ? 'text-primary' : 'text-muted'
                }`}
              />
              <div className='flex-fill'>
                <div className='fw-semibold small mb-1'>
                  Collaborative (Read-Only)
                </div>
                <div
                  className='text-muted'
                  style={{ fontSize: '0.8rem', lineHeight: '1.4' }}
                >
                  They can view your trip but cannot make changes
                </div>
              </div>
            </div>
          </button>

          <button
            type='button'
            onClick={() => setSharingMode('edit')}
            className={`btn text-start p-3 border ${
              sharingMode === 'edit'
                ? 'border-primary border-2 bg-primary-subtle'
                : 'border-secondary'
            }`}
          >
            <div className='d-flex align-items-start gap-2'>
              <Edit3
                size={18}
                className={`flex-shrink-0 mt-1 ${
                  sharingMode === 'edit' ? 'text-primary' : 'text-muted'
                }`}
              />
              <div className='flex-fill'>
                <div className='fw-semibold small mb-1'>
                  Collaborative (Full Access)
                </div>
                <div
                  className='text-muted'
                  style={{ fontSize: '0.8rem', lineHeight: '1.4' }}
                >
                  You both can co-plan and edit the same trip together in
                  real-time
                </div>
              </div>
            </div>
          </button>
        </div>
      </div>

      <form onSubmit={handleShare}>
        <div className='mb-3'>
          <label
            htmlFor='email'
            className='form-label fw-medium'
          >
            Recipient Email(s)
          </label>
          <div className='position-relative'>
            <Mail
              size={18}
              className='position-absolute text-muted'
              style={{
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none'
              }}
            />
            <input
              type='text'
              id='email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder='friend@example.com, another@example.com'
              required
              className='form-control ps-5'
            />
          </div>
          <div className='form-text'>
            Separate multiple emails with commas. New users will be prompted to
            create an account.
          </div>
        </div>

        {message && (
          <div
            className='alert alert-info d-flex align-items-center gap-2'
            role='alert'
          >
            <CheckCircle
              size={18}
              className='flex-shrink-0'
            />
            <span className='small'>{message}</span>
          </div>
        )}

        <div className='d-flex justify-content-between gap-3'>
          <Button
            type='button'
            onClick={() => onClose()}
            variant='dark'
            width={180}
            size='md'
          >
            Cancel
          </Button>
          <Button
            type='submit'
            disabled={loading}
            variant='primary'
            width={180}
            size='md'
          >
            {loading
              ? 'Sending...'
              : sharingMode === 'copy'
              ? 'Send Copy'
              : 'Send Invitation'}
          </Button>
        </div>
      </form>

      {pendingInvitations.length > 0 && (
        <div className='mt-4 p-3 rounded border bg-warning-subtle'>
          <div className='d-flex align-items-center gap-2 mb-3'>
            <Clock
              size={16}
              className='text-warning'
            />
            <h6 className='fw-semibold mb-0'>
              Pending Invitations ({pendingInvitations.length})
            </h6>
          </div>
          <div className='d-flex flex-column gap-2'>
            {pendingInvitations.map((invitation) => (
              <div
                key={invitation.id}
                className='d-flex align-items-center justify-content-between p-2 bg-white rounded border'
              >
                <div className='d-flex align-items-center gap-2 flex-fill'>
                  <Mail
                    size={14}
                    className='text-muted flex-shrink-0'
                  />
                  <div className='flex-fill text-truncate'>
                    <p
                      className='fw-medium mb-0 text-truncate'
                      style={{ fontSize: '0.85rem' }}
                    >
                      {invitation.recipient_email}
                    </p>
                    <div className='d-flex align-items-center gap-1'>
                      {invitation.sharing_mode === 'copy' ? (
                        <>
                          <CopyIcon
                            size={10}
                            className='text-muted'
                          />
                          <span
                            className='text-muted'
                            style={{ fontSize: '0.7rem' }}
                          >
                            Copy
                          </span>
                        </>
                      ) : invitation.sharing_mode === 'read' ? (
                        <>
                          <Eye
                            size={10}
                            className='text-muted'
                          />
                          <span
                            className='text-muted'
                            style={{ fontSize: '0.7rem' }}
                          >
                            Read-Only
                          </span>
                        </>
                      ) : (
                        <>
                          <Edit3
                            size={10}
                            className='text-muted'
                          />
                          <span
                            className='text-muted'
                            style={{ fontSize: '0.7rem' }}
                          >
                            Full Access
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  onClick={() => handleCancelInvitation(invitation.id)}
                  disabled={cancelingId === invitation.id}
                  variant='transparent'
                  size='sm'
                  className='text-danger p-1'
                  title='Cancel invitation'
                >
                  <X size={14} />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {onOpenPublicSettings && (
        <div className='mt-4 p-3 bg-light rounded border'>
          <div className='d-flex align-items-center justify-content-between'>
            <div className='d-flex align-items-center gap-2'>
              <Link2
                size={18}
                className='text-secondary'
              />
              <div>
                <h6 className='fw-semibold mb-1'>Public Link Settings</h6>
                <p className='text-muted mb-0 small'>
                  Configure public sharing via link and QR code
                </p>
              </div>
            </div>
            <Button
              onClick={() => {
                onClose();
                onOpenPublicSettings();
              }}
              variant='primary'
              size='sm'
            >
              <Settings
                size={14}
                className='me-1'
              />
              Settings
            </Button>
          </div>
        </div>
      )}
    </ModalCustom>
  );
}

export default ShareInviteModal;
